/**
 * VoiceSettingsEditor Component
 *
 * Edits voice settings: stability, similarity boost, style.
 * Uses sliders for intuitive adjustment.
 */

import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Loader2, RotateCcw } from 'lucide-react';
import type { IVoiceSettingsEditorProps, IVoiceSettings } from '@/@types/voices';
import { VOICE_SETTINGS_CONFIG, VOICE_CONSTANTS } from '@/@types/voices';

function VoiceSettingsEditor({
  voiceId: _voiceId,
  settings,
  onSave,
  isSaving,
}: IVoiceSettingsEditorProps) {
  // voiceId reserved for future use (e.g., preview generation)
  const [localSettings, setLocalSettings] = useState<IVoiceSettings>(() => settings);
  const [hasChanges, setHasChanges] = useState(false);

  // Reset local state when the settings prop changes (e.g., switching voices).
  // Storing the committed settings as state lets us detect prop changes during render
  // without refs or effects (React's derived-state pattern).
  const [committedSettings, setCommittedSettings] = useState<IVoiceSettings>(settings);
  if (settings !== committedSettings) {
    setCommittedSettings(settings);
    setLocalSettings(settings);
    if (hasChanges) setHasChanges(false);
  }

  const handleSliderChange = useCallback(
    (key: keyof Pick<IVoiceSettings, 'stability' | 'similarityBoost' | 'style'>) =>
      (value: number[]) => {
        setLocalSettings(prev => ({
          ...prev,
          [key]: value[0],
        }));
        setHasChanges(true);
      },
    []
  );

  const handleSpeakerBoostChange = useCallback((checked: boolean) => {
    setLocalSettings(prev => ({
      ...prev,
      useSpeakerBoost: checked,
    }));
    setHasChanges(true);
  }, []);

  const handleReset = useCallback(() => {
    setLocalSettings(VOICE_CONSTANTS.DEFAULT_VOICE_SETTINGS);
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(async () => {
    await onSave(localSettings);
    setHasChanges(false);
  }, [localSettings, onSave]);

  const handleCancel = useCallback(() => {
    setLocalSettings(settings);
    setHasChanges(false);
  }, [settings]);

  return (
    <div className="space-y-6">
      {/* Stability Slider */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <Label className="text-sm font-medium">{VOICE_SETTINGS_CONFIG.stability.label}</Label>
          <span className="text-sm text-gray-500">
            {(localSettings.stability * 100).toFixed(0)}%
          </span>
        </div>
        <Slider
          value={[localSettings.stability]}
          onValueChange={handleSliderChange('stability')}
          min={VOICE_SETTINGS_CONFIG.stability.min}
          max={VOICE_SETTINGS_CONFIG.stability.max}
          step={VOICE_SETTINGS_CONFIG.stability.step}
          disabled={isSaving}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {VOICE_SETTINGS_CONFIG.stability.description}
        </p>
      </div>

      {/* Similarity Boost Slider */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <Label className="text-sm font-medium">
            {VOICE_SETTINGS_CONFIG.similarityBoost.label}
          </Label>
          <span className="text-sm text-gray-500">
            {(localSettings.similarityBoost * 100).toFixed(0)}%
          </span>
        </div>
        <Slider
          value={[localSettings.similarityBoost]}
          onValueChange={handleSliderChange('similarityBoost')}
          min={VOICE_SETTINGS_CONFIG.similarityBoost.min}
          max={VOICE_SETTINGS_CONFIG.similarityBoost.max}
          step={VOICE_SETTINGS_CONFIG.similarityBoost.step}
          disabled={isSaving}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {VOICE_SETTINGS_CONFIG.similarityBoost.description}
        </p>
      </div>

      {/* Style Slider */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <Label className="text-sm font-medium">{VOICE_SETTINGS_CONFIG.style.label}</Label>
          <span className="text-sm text-gray-500">{(localSettings.style * 100).toFixed(0)}%</span>
        </div>
        <Slider
          value={[localSettings.style]}
          onValueChange={handleSliderChange('style')}
          min={VOICE_SETTINGS_CONFIG.style.min}
          max={VOICE_SETTINGS_CONFIG.style.max}
          step={VOICE_SETTINGS_CONFIG.style.step}
          disabled={isSaving}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {VOICE_SETTINGS_CONFIG.style.description}
        </p>
      </div>

      {/* Speaker Boost Toggle */}
      <div className="flex items-center justify-between py-3 border-t border-gray-200 dark:border-gray-700">
        <div>
          <Label className="text-sm font-medium">Speaker Boost</Label>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Enhances speaker similarity for more accurate voice cloning
          </p>
        </div>
        <Switch
          checked={localSettings.useSpeakerBoost}
          onCheckedChange={handleSpeakerBoostChange}
          disabled={isSaving}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="ghost" size="sm" onClick={handleReset} disabled={isSaving}>
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
          Reset to Defaults
        </Button>

        <div className="flex gap-2">
          {hasChanges && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
          )}
          <Button type="button" size="sm" onClick={handleSave} disabled={!hasChanges || isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export { VoiceSettingsEditor };
